/**
 * Number, percentage and duration rendering. One implementation each, because a
 * figure that appears twice must read the same both times (§9.12).
 *
 * Every figure is rendered under `font-variant-numeric: tabular-nums`
 * (`.pv-figure` / the `tabular` utility) — see `design/tokens.css`.
 */

const NBSP = ' ';

/**
 * Whether a sensor's raw distance reading is physically plausible — the EM400-MUD's mounting
 * range, not the per-bay calibration band (`200-6000mm`, and not the `65533`/`65535` sentinels).
 * Found by /code-review, not assumed: three call sites (this one's caller, `BayDrawer.tsx`, the
 * mock handler) each had their own copy of these four numbers before this — one shared
 * definition now, matching `pv-app`'s own `DevicePlausibility.IsPlausible` (`Calibration.cs`).
 */
export function isPlausibleDistance(distanceMm: number | null | undefined): boolean {
  return (
    distanceMm != null &&
    distanceMm !== 65533 &&
    distanceMm !== 65535 &&
    distanceMm >= 200 &&
    distanceMm <= 6000
  );
}

/** `1 234`. Thin space grouping, so a five-digit count does not read as two. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-GB', { useGrouping: true }).format(value);
}

/** `77.9 %` — one decimal, the precision the seed and the figures use. */
export function formatPercent(value: number, fractionDigits = 1): string {
  return `${value.toFixed(fractionDigits)}${NBSP}%`;
}

/** `+18` / `−4` / `0`. A comparison with no sign is not a comparison. */
export function formatDelta(value: number): string {
  if (value === 0) return '0';
  return value > 0 ? `+${formatCount(value)}` : `−${formatCount(Math.abs(value))}`;
}

/** `+2.4 pts` — the unit for a percentage-point comparison, never `%`. */
export function formatPointsDelta(value: number, fractionDigits = 1): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(fractionDigits)}${NBSP}pts`;
}

export type DurationStyle =
  /** `2 d 11 h 08 m` — the bay drawer's Duration row. */
  | 'long'
  /** `2 h 10` — the compact form beside an "occupied since" timestamp. */
  | 'compact'
  /** `2 d 11 h` — the coarsest form, for an age in prose. */
  | 'coarse';

interface DurationParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function splitDuration(totalSeconds: number): DurationParts {
  const s = Math.max(0, Math.floor(totalSeconds));
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

/**
 * `2 d 11 h 08 m`, `2 h 10`, `2 d 11 h`.
 *
 * Under a minute the answer is `< 1 m` rather than `0 m`, because zero reads as
 * "no data" and "less than a minute" reads as "just now".
 */
export function formatDuration(totalSeconds: number, style: DurationStyle = 'long'): string {
  const { days, hours, minutes } = splitDuration(totalSeconds);

  if (style === 'compact') {
    // `2 h 10` — hours and zero-padded minutes, days folded into hours.
    const h = days * 24 + hours;
    if (h === 0 && minutes === 0) return `<${NBSP}1${NBSP}m`;
    if (h === 0) return `${minutes}${NBSP}m`;
    return `${h}${NBSP}h${NBSP}${String(minutes).padStart(2, '0')}`;
  }

  if (style === 'coarse') {
    if (days > 0) return `${days}${NBSP}d${NBSP}${hours}${NBSP}h`;
    if (hours > 0) return `${hours}${NBSP}h${NBSP}${minutes}${NBSP}m`;
    if (minutes > 0) return `${minutes}${NBSP}m`;
    // Seconds rather than "< 1 m": the staleness banner can legitimately be
    // twenty seconds old, and "< 1 m old" would read as a rounding error.
    return `${splitDuration(totalSeconds).seconds}${NBSP}s`;
  }

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}${NBSP}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}${NBSP}h`);
  parts.push(`${String(minutes).padStart(2, '0')}${NBSP}m`);
  if (parts.length === 1 && minutes === 0) return `<${NBSP}1${NBSP}m`;
  return parts.join(' ');
}

/** `12 min ago`, `2 d 11 h ago`, `just now`. */
export function formatAge(totalSeconds: number): string {
  if (totalSeconds < 10) return 'just now';
  if (totalSeconds < 60) return `${Math.floor(totalSeconds)} seconds ago`;
  const { days, hours, minutes } = splitDuration(totalSeconds);
  if (days === 0 && hours === 0) return `${minutes} min ago`;
  return `${formatDuration(totalSeconds, 'coarse')} ago`;
}

/** DevEUI is stored lowercase hex with no separators and displayed uppercase. */
export function formatDevEui(devEui: string): string {
  return devEui.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
}

/**
 * A zone's display label. Area-level zones have no separate friendly name in
 * the tender's own facility table (`db/009_seed_facility.sql` sets `name =
 * code` for them) — showing both would double the label, e.g.
 * "PROMANADE-4 PROMANADE-4".
 */
export function zoneLabel(zone: { code: string; name: string }): string {
  return zone.code === zone.name ? zone.code : `${zone.code} ${zone.name}`;
}

/** The map object's label: the numeric segment of `6-A-014` at two digits. */
export function bayObjectLabel(bayCode: string): string {
  const numeric = bayCode.slice(bayCode.lastIndexOf('-') + 1);
  return numeric.replace(/^0+(?=\d\d)/, '');
}

/**
 * `74•••1`. Plates are personal data: the drawer and the alert context show a
 * masked form, and every view of the unmasked value writes an audit row (§9.7).
 */
export function maskPlate(plate: string): string {
  const trimmed = plate.replace(/\s+/g, '');
  if (trimmed.length <= 2) return '•'.repeat(trimmed.length);
  const head = trimmed.slice(0, 2);
  const tail = trimmed.slice(-1);
  return `${head}${'•'.repeat(Math.max(1, trimmed.length - 3))}${tail}`;
}

/** `2.4 MB`, `58.1 KB` — the `/live/bays` payload budget is quoted in these. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}${NBSP}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}${NBSP}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}${NBSP}MB`;
}
